type TikTokPayload = Record<string, unknown>

type TikTokTracker = {
  track?: (event: string, payload?: TikTokPayload) => void
}

type TikTokFunnelEvent = "ViewContent" | "AddToCart" | "InitiateCheckout" | "Purchase"

export type TikTokProductContext = {
  packType?: string | null
  skuCode?: string | null
  name?: string | null
  valueMxn?: number | null
}

const PENDING_PURCHASE_KEY = "faceshot.tiktok.pendingPurchase"

const ROUTE_EVENT_NOTE = {
  pricingPage: "Route /pricing and pricing bundles modal trigger ViewContent.",
  addCreditsClick: "Bundle buy click triggers AddToCart.",
  checkoutRedirect: "Stripe redirect start triggers InitiateCheckout.",
  successPage: "Route /success after Stripe return triggers Purchase.",
}

export function getTikTokRouteEventNote(): Record<string, string> {
  return ROUTE_EVENT_NOTE
}

function getTracker(): TikTokTracker | null {
  if (typeof window === "undefined") return null
  const maybeTracker = (window as Window & { ttq?: TikTokTracker }).ttq
  return maybeTracker && typeof maybeTracker.track === "function" ? maybeTracker : null
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

export function resolveTikTokContentId(context: Pick<TikTokProductContext, "packType" | "skuCode">): string | null {
  return toNonEmptyString(context.packType) ?? toNonEmptyString(context.skuCode)
}

function normalizeMxnValue(value: unknown): number | null {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Number(parsed.toFixed(2))
}

function buildPayload(event: TikTokFunnelEvent, context: TikTokProductContext): TikTokPayload | null {
  const contentId = resolveTikTokContentId(context)
  if (!contentId) {
    console.warn("[TikTok Pixel] Skipping event because content_id is missing", {
      event,
      packType: context.packType,
      skuCode: context.skuCode,
    })
    return null
  }

  const contentName = toNonEmptyString(context.name)
  const valueMxn = normalizeMxnValue(context.valueMxn)
  const contentsItem: Record<string, unknown> = { content_id: contentId }
  if (contentName) {
    contentsItem.content_name = contentName
  }

  const payload: TikTokPayload = {
    content_id: contentId,
    content_ids: [contentId],
    content_type: "product",
    contents: [contentsItem],
    currency: "MXN",
  }

  if (valueMxn != null) {
    payload.value = valueMxn
  }

  return payload
}

export function trackTikTokFunnelEvent(event: TikTokFunnelEvent, context: TikTokProductContext): boolean {
  const tracker = getTracker()
  if (!tracker?.track) return false
  const payload = buildPayload(event, context)
  if (!payload) return false
  tracker.track(event, payload)
  return true
}

export function savePendingTikTokPurchase(context: TikTokProductContext): void {
  if (typeof window === "undefined") return
  const contentId = resolveTikTokContentId(context)
  if (!contentId) {
    console.warn("[TikTok Pixel] Skipping pending purchase save because content_id is missing", {
      packType: context.packType,
      skuCode: context.skuCode,
    })
    return
  }

  const payload = {
    contentId,
    name: toNonEmptyString(context.name),
    valueMxn: normalizeMxnValue(context.valueMxn),
    currency: "MXN",
    savedAt: Date.now(),
  }

  try {
    window.sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(payload))
  } catch {
    return
  }
}

export function consumePendingTikTokPurchase(): {
  contentId: string
  name: string | null
  valueMxn: number | null
  currency: "MXN"
} | null {
  if (typeof window === "undefined") return null
  const raw = window.sessionStorage.getItem(PENDING_PURCHASE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as {
      contentId?: unknown
      name?: unknown
      valueMxn?: unknown
      currency?: unknown
    }
    window.sessionStorage.removeItem(PENDING_PURCHASE_KEY)
    const contentId = toNonEmptyString(parsed.contentId)
    if (!contentId) return null
    return {
      contentId,
      name: toNonEmptyString(parsed.name),
      valueMxn: normalizeMxnValue(parsed.valueMxn),
      currency: "MXN",
    }
  } catch {
    window.sessionStorage.removeItem(PENDING_PURCHASE_KEY)
    return null
  }
}
